import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronDown,
  FiChevronUp,
  FiEdit,
  FiEye,
  FiRefreshCw,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';

// Type-cast icon components for TypeScript compatibility
const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiEditComponent = FiEdit as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// const FiMoreVerticalComponent = FiMoreVertical as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;

const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiRefreshCwComponent = FiRefreshCw as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Interface for campaign data from API
interface Campaign {
  id: string;
  orgId: string;
  topicId: string | null;
  segmentSnapshotIds: string | null;
  transactional: boolean;
  title: string;
  subject: string | null;
  type: string | null;
  status: 'DRAFT' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
  templateId: string;
  senderProfileId: string;
  segmentIds: string | null;
  tagIds: string[] | null;
  senderIds: string | null;
  sendMode: 'SCHEDULE' | 'SEND_NOW' | null;
  scheduledAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  approvedBy: string | null;
  scheduledBy: string | null;
  createdAt: string;
  updatedAt: string;
  abEnabled: boolean;
  abType: string | null;
  abWinnerMetric: string | null;
  abEvalWindowHours: number | null;
  abTestSamplePct: number | null;
  deletedAt: string | null;
  template: {
    id: string;
    orgId: string;
    name: string;
    description: string;
    status: string;
    version: number;
    folderId: string | null;
    assetId: string | null;
    blocks: Array<{
      text: string;
      type: string;
    }>;
    styles: string;
    metadata: {
      category: string;
      tags: string[];
      ownership: {
        type: string;
        orgId: string;
        userId: string;
      };
      createdBy: string;
      createdAt: string;
      updatedAt: string;
    };
    capability: string | null;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    thumbnailUrl: string | null;
  };
  senderProfile: {
    id: string;
    orgId: string;
    senderName: string;
    senderEmail: string;
    verified: boolean;
    createdAt: string;
    deletedAt: string | null;
  };
  segments: any[];
  tags: any[];
  matrics: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    openRate: number;
    clickRate: number;
    bounced: number;
    unsubscribed: number;
  };
}

// Interface for API response
interface ApiResponse {
  content: Campaign[];
  page: number;
  size: number;
  totalCount: number;
  totalPages: number;
  last: boolean;
}

// Props for the CampaignsTable component
interface CampaignsTableProps {
  // No props needed as we fetch data ourselves
}

// Utility function to determine status badge color based on campaign status
const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-600';
    case 'DRAFT':
      return 'bg-red-100 text-red-600';
    case 'SCHEDULED':
      return 'bg-yellow-100 text-yellow-600';
    case 'COMPLETED':
      return 'bg-blue-100 text-blue-600';
    case 'CANCELLED':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// Utility function to format date
const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

// Utility function to format percentage
const formatPercentage = (value: number) => {
  if (value === undefined || value === null || !isFinite(value)) return '-';
  return `${value.toFixed(1)}%`;
};

// Utility function to format date and time
const formatDateTime = (dateString: string) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

// ⭐ Tooltip component for segments
const SegmentsTooltip: React.FC<{ segments: any[]; children: React.ReactNode }> = ({ segments, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const getSegmentNames = () => {
    if (!segments || segments.length === 0) return [];
    return segments.map(s => s.name || 'Unnamed');
  };

  const segmentNames = getSegmentNames();
  const displayText = segmentNames.join(', ');

  const handleMouseEnter = () => {
    if (segmentNames.length === 0) return;

    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      // Position tooltip above the text
      const tooltipHeight = tooltipRef.current?.offsetHeight || 100;
      setPosition({
        top: rect.top - tooltipHeight - 10,
        left: rect.left + rect.width / 2,
      });
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  if (segmentNames.length === 0) {
    return <span>-</span>;
  }

  return (
    <div
      ref={triggerRef}
      className="relative inline-block w-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="truncate max-w-[110px] cursor-pointer hover:text-blue-600">
        {displayText}
      </div>

      {isVisible && segmentNames.length > 0 && (
        <div
          ref={tooltipRef}
          className="fixed z-50 bg-gray-800 text-white text-sm rounded-lg shadow-lg p-3 max-w-xs pointer-events-none"
          style={{
            top: position.top,
            left: position.left,
            transform: 'translateX(-50%)',
            minWidth: '150px',
            maxWidth: '300px',
          }}
        >
          <div className="font-medium text-xs text-gray-300 mb-1">Segments:</div>
          <div className="space-y-1">
            {segmentNames.map((name, index) => (
              <div key={index} className="text-sm">
                {index + 1}. {name}
              </div>
            ))}
          </div>
          {/* Arrow pointing down */}
          <div
            className="absolute w-3 h-3 bg-gray-800 transform rotate-45"
            style={{
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
            }}
          />
        </div>
      )}
    </div>
  );
};

// Main CampaignsTable component
const CampaignsTable: React.FC<CampaignsTableProps> = () => {
  const navigate = useNavigate();
  // const location = useLocation();

  // API States
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(6);

  // State for refresh
  const [isRefreshing, setIsRefreshing] = useState(false);

  // State for filtering
  const [activeTab, setActiveTab] = useState<
    'All' | 'DRAFT' | 'SCHEDULED' | 'COMPLETED'
  >('All');

  // State for sorting
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Campaign;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Refs to prevent duplicate requests
  const isInitialLoadDone = useRef(false);
  const fetchInProgress = useRef(false);
  const lastFetchedPage = useRef<number>(-1);

  // Get authentication context
  const getHeaders = useCallback(() => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token || !tenantId) {
      throw new Error('Authentication required');
    }
    return {
      'X-Tenant-Id': tenantId,
      'X-User-Id': userId || '',
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  // Fetch campaigns from API with pagination
  const fetchCampaigns = useCallback(async (page: number = 0) => {
    if (fetchInProgress.current) {
      return;
    }

    if (page < 0) {
      return;
    }

    if (lastFetchedPage.current === page && isInitialLoadDone.current) {
      return;
    }

    fetchInProgress.current = true;
    setLoading(true);

    try {

      const headers = getHeaders();
      const response = await fetch(
        `${BASE_URL}/v1/campaign-wizard?page=${page}&size=${pageSize}`,
        {
          method: 'GET',
          headers: headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: ApiResponse = await response.json();

      // Filter campaigns based on active tab
      let filteredContent = data.content;
      if (activeTab !== 'All') {
        filteredContent = data.content.filter(
          (campaign) => campaign.status === activeTab
        );
      }

      setCampaigns(filteredContent);
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);
      lastFetchedPage.current = page;

    } catch (error) {
      // console.error('❌ Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, [pageSize, activeTab, getHeaders]);

  // Initial load
  useEffect(() => {
    if (!isInitialLoadDone.current) {
      fetchCampaigns(0);
      isInitialLoadDone.current = true;
    }
  }, [fetchCampaigns]);

  // Handle tab change
  const handleTabChange = useCallback((tab: typeof activeTab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    lastFetchedPage.current = -1;
  }, []);

  // Handle refresh - re-fetches current page
  const handleRefresh = useCallback(async () => {
    if (isRefreshing || loading || fetchInProgress.current) return;

    setIsRefreshing(true);
    // Reset the last fetched page so fetchCampaigns actually re-fetches
    lastFetchedPage.current = -1;

    try {
      await fetchCampaigns(currentPage);
    } catch (err) {
      // fetchCampaigns already handles its own errors/toasts
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, loading, currentPage, fetchCampaigns]);

  // Handle sorting
  const requestSort = useCallback((key: keyof Campaign) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  // Get sort icon
  const getSortIcon = useCallback((key: keyof Campaign) => {
    if (sortConfig?.key === key) {
      return sortConfig.direction === 'asc' ? (
        <FiChevronUpComponent size={14} />
      ) : (
        <FiChevronDownComponent size={14} />
      );
    }
    return <FiChevronDownComponent size={14} />;
  }, [sortConfig]);

  // Sort campaigns client-side after filtering
  const sortedCampaigns = useMemo(() => {
    if (!sortConfig) return campaigns;
    return [...campaigns].sort((a, b) => {
      let valA: any = a[sortConfig.key];
      let valB: any = b[sortConfig.key];

      // Handle date fields
      if (sortConfig.key === 'updatedAt' || sortConfig.key === 'createdAt' || sortConfig.key === 'scheduledAt') {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      } else {
        valA = valA?.toString()?.toLowerCase() ?? '';
        valB = valB?.toString()?.toLowerCase() ?? '';
      }

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [campaigns, sortConfig]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 0 || newPage >= totalPages) {
      return;
    }

    if (newPage === currentPage) {
      return;
    }

    if (loading || fetchInProgress.current) {
      return;
    }

    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, totalPages, loading]);

  // Fetch new page when currentPage changes
  useEffect(() => {
    if (isInitialLoadDone.current && currentPage !== lastFetchedPage.current) {
      fetchCampaigns(currentPage);
    }
  }, [currentPage, fetchCampaigns]);

  // Get status count for tabs - using totalCount from API
  // const getStatusCount = useCallback((status: string) => {
  //   if (status === 'All') return totalCount;
  //   return campaigns.filter((c) => c.status === status).length;
  // }, [campaigns, totalCount]);

  // Calculate which items to show
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  return (
    <div className='bg-white rounded-md mt-6 border border-gray-200 shadow-sm'>
      {/* Tabs and Refresh */}
      <div className='flex justify-between items-center px-6 pt-4'>
        <div className='flex space-x-6'>
          {['All', 'DRAFT', 'SCHEDULED', 'COMPLETED'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab as typeof activeTab)}
              className={`pb-2 font-medium flex items-center pl-2 ${activeTab === tab
                ? 'border-b-2 border-teal-500 text-teal-600'
                : 'text-gray-500'
                }`}
              style={{ fontSize: '14px', fontWeight: 'normal' }}
            >
              {tab === 'All' ? 'All' : tab.charAt(0) + tab.slice(1).toLowerCase()}
              <span
                className='flex items-center justify-center mr-2 ml-1.5'
                style={{
                  width: '30px',
                  height: '21px',
                  backgroundColor: activeTab === tab ? '#E0F8FB' : '#F5F8FA',
                  borderRadius: '40px',
                }}
              >
                {tab === 'All' ? totalCount : campaigns.filter((c) => c.status === tab).length}
              </span>
            </button>
          ))}
        </div>

        {/* ⭐ Refresh button */}
        <div className='flex items-center gap-2'>
          <button
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
            className={`p-2 text-gray-500 hover:text-teal-600 transition-colors ${loading || isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            title="Refresh data"
          >
            <FiRefreshCwComponent
              size={18}
              className={loading || isRefreshing ? 'animate-spin' : ''}
            />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className='px-6 pb-4'>
        {loading ? (
          <div className='flex justify-center items-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500'></div>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table
              className='w-full text-left mt-4 border border-gray-200'
              style={{ minWidth: '1350px' }}
            >
              <thead className='bg-gray-100'>
                <tr
                  className='text-gray-600 text-sm border-b'
                  style={{
                    fontSize: '12px',
                    color: '#657584',
                  }}
                >
                  <th
                    className='px-6 py-3 cursor-pointer font-normal sticky left-0 bg-gray-100 z-20 min-w-[180px]'
                    onClick={() => requestSort('title')}
                  >
                    <div className='flex items-center gap-1 whitespace-nowrap'>
                      CAMPAIGN NAME {getSortIcon('title')}
                    </div>
                  </th>
                  <th
                    className='px-6 py-3 cursor-pointer font-normal min-w-[140px]'
                    onClick={() => requestSort('type')}
                  >
                    <div className='flex items-center gap-1 whitespace-nowrap'>
                      CAMPAIGN TYPE {getSortIcon('type')}
                    </div>
                  </th>
                  <th className='px-6 py-3 font-normal min-w-[160px]'>
                    <div className='whitespace-nowrap'>SUBJECT LINE</div>
                  </th>
                  <th
                    className='px-6 py-3 cursor-pointer font-normal min-w-[150px]'
                    onClick={() => requestSort('scheduledAt')}
                  >
                    <div className='flex items-center gap-1 whitespace-nowrap'>
                      CAMPAIGN LIVE DATE {getSortIcon('scheduledAt')}
                    </div>
                  </th>
                  <th className='px-6 py-3 font-normal min-w-[80px]'>
                    <div className='whitespace-nowrap'>SENT %</div>
                  </th>
                  <th className='px-6 py-3 font-normal min-w-[100px]'>
                    <div className='whitespace-nowrap'>DELIVERED %</div>
                  </th>
                  <th className='px-6 py-3 font-normal min-w-[80px]'>
                    <div className='whitespace-nowrap'>OPEN %</div>
                  </th>
                  <th className='px-6 py-3 font-normal min-w-[80px]'>
                    <div className='whitespace-nowrap'>CLICK %</div>
                  </th>
                  <th className='px-6 py-3 font-normal min-w-[90px]'>
                    <div className='whitespace-nowrap'>BOUNCE %</div>
                  </th>
                  <th className='px-6 py-3 font-normal min-w-[120px]'>
                    <div className='whitespace-nowrap'>UNSUBSCRIBED %</div>
                  </th>
                  <th className='px-6 py-3 font-normal min-w-[120px]'>
                    <div className='whitespace-nowrap'>SEGMENTS</div>
                  </th>
                  <th
                    className='px-6 py-3 cursor-pointer font-normal min-w-[100px]'
                    onClick={() => requestSort('status')}
                  >
                    <div className='flex items-center gap-1 whitespace-nowrap'>
                      STATUS {getSortIcon('status')}
                    </div>
                  </th>
                  <th
                    className='px-6 py-3 cursor-pointer font-normal min-w-[150px]'
                    onClick={() => requestSort('updatedAt')}
                  >
                    <div className='flex items-center gap-1 whitespace-nowrap'>
                      LAST UPDATED {getSortIcon('updatedAt')}
                    </div>
                  </th>
                  <th className='px-6 py-3 font-normal sticky right-0 bg-gray-100 z-20 min-w-[120px]'>
                    <div className='whitespace-nowrap'>ACTIONS</div>
                  </th>
                </tr>
              </thead>
              <tbody
                style={{
                  color: '#3F5265',
                  fontSize: '14px',
                }}
              >
                {sortedCampaigns.length > 0 ? (
                  sortedCampaigns.map((c) => {
                    // Calculate percentages
                    const sent = c.matrics?.sent || 0;
                    const delivered = c.matrics?.delivered || 0;
                    const opened = c.matrics?.opened || 0;
                    const clicked = c.matrics?.clicked || 0;
                    const bounced = c.matrics?.bounced || 0;
                    const unsubscribed = c.matrics?.unsubscribed || 0;

                    const sentPercent = sent > 0 ? (sent / sent) * 100 : 0;
                    const deliveredPercent = sent > 0 ? (delivered / sent) * 100 : 0;
                    const openPercent = sent > 0 ? (opened / sent) * 100 : 0;
                    const clickPercent = sent > 0 ? (clicked / sent) * 100 : 0;
                    const bouncePercent = sent > 0 ? (bounced / sent) * 100 : 0;
                    const unsubscribePercent =
                      sent > 0 ? (unsubscribed / sent) * 100 : 0;

                    return (
                      <tr key={c.id} className='border-b hover:bg-gray-50'>
                        <td className='px-6 py-4 sticky left-0 bg-white z-10 min-w-[180px]'>
                          <div className='truncate'>{c.title || 'Untitled'}</div>
                        </td>
                        <td className='px-6 py-4 min-w-[140px]'>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap`}
                            style={{
                              backgroundColor: '#F5F8FA',
                            }}
                          >
                            {c.type || 'General'}
                          </span>
                        </td>
                        <td className='px-6 py-4 min-w-[160px]'>
                          <div className='truncate max-w-[150px]'>
                            {c.subject || '-'}
                          </div>
                        </td>
                        <td className='px-6 py-4 min-w-[150px]'>
                          <div className='whitespace-nowrap'>
                            {c.scheduledAt ? formatDate(c.scheduledAt) : '-'}
                          </div>
                        </td>
                        <td className='px-6 py-4 min-w-[80px]'>
                          <div className='whitespace-nowrap'>
                            {formatPercentage(sentPercent)}
                          </div>
                        </td>
                        <td className='px-6 py-4 min-w-[100px]'>
                          <div className='whitespace-nowrap'>
                            {formatPercentage(deliveredPercent)}
                          </div>
                        </td>
                        <td className='px-6 py-4 min-w-[80px]'>
                          <div className='whitespace-nowrap'>
                            {formatPercentage(openPercent)}
                          </div>
                        </td>
                        <td className='px-6 py-4 min-w-[80px]'>
                          <div className='whitespace-nowrap'>
                            {formatPercentage(clickPercent)}
                          </div>
                        </td>
                        <td className='px-6 py-4 min-w-[90px]'>
                          <div className='whitespace-nowrap'>
                            {formatPercentage(bouncePercent)}
                          </div>
                        </td>
                        <td className='px-6 py-4 min-w-[120px]'>
                          <div className='whitespace-nowrap'>
                            {formatPercentage(unsubscribePercent)}
                          </div>
                        </td>
                        {/* ⭐ Segments column with tooltip */}
                        <td className='px-6 py-4 min-w-[120px]'>
                          <SegmentsTooltip segments={c.segments || []}>
                            <span className="truncate max-w-[110px]">
                              {c.segments && c.segments.length > 0
                                ? c.segments.map((s) => s.name || 'Unnamed').join(', ')
                                : '-'}
                            </span>
                          </SegmentsTooltip>
                        </td>
                        <td className='px-6 py-4 min-w-[100px]'>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(
                              c.status
                            )}`}
                          >
                            {c.status
                              ? c.status.charAt(0) + c.status.slice(1).toLowerCase()
                              : 'Unknown'}
                          </span>
                        </td>
                        <td className='px-6 py-4 min-w-[150px]'>
                          <div className='whitespace-nowrap text-xs text-gray-500'>
                            {formatDateTime(c.updatedAt)}
                          </div>
                        </td>
                        <td className='px-6 py-4 sticky right-0 bg-white z-10 min-w-[120px]'>
                          <div className='flex items-center space-x-2'>
                            {c.status === 'COMPLETED' ? (
                              <button
                                className='text-teal-600 flex items-center gap-1 hover:text-teal-800 whitespace-nowrap text-sm'
                                onClick={() =>
                                  navigate(`/campaigns/view/${c.id}`, {
                                    state: { campaign: c },
                                  })
                                }
                              >
                                <FiEyeComponent size={16} /> View
                              </button>
                            ) : c.status === 'DRAFT' || c.status === 'SCHEDULED' ? (
                              <button
                                className='text-blue-600 flex items-center gap-1 hover:text-blue-800 whitespace-nowrap text-sm'
                                onClick={() =>
                                  navigate(`/campaigns/edit/${c.id}`, {
                                    state: { campaign: c },
                                  })
                                }
                              >
                                <FiEditComponent size={16} /> Edit
                              </button>
                            ) : (
                              <button
                                className='text-teal-600 flex items-center gap-1 hover:text-teal-800 whitespace-nowrap text-sm'
                                onClick={() =>
                                  navigate(`/campaigns/view/${c.id}`, {
                                    state: { campaign: c },
                                  })
                                }
                              >
                                <FiEyeComponent size={16} /> View
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={14} className='px-6 py-6 text-center text-gray-500'>
                      No campaigns found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer with Modern Pagination - Same style as TemplatesGallery */}
      {!loading && totalPages > 0 && (
        <div className='flex items-center justify-between px-6 py-4 text-sm text-gray-500 border-t border-gray-200'>
          <span>
            Showing {startItem} to {endItem} of {totalCount} campaigns
          </span>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {(() => {
                const pages = [];
                const total = totalPages;
                const current = currentPage + 1;
                const delta = 1;

                pages.push(1);

                let rangeStart = Math.max(2, current - delta);
                let rangeEnd = Math.min(total - 1, current + delta);

                if (rangeStart > 2) {
                  pages.push('...');
                }

                for (let i = rangeStart; i <= rangeEnd; i++) {
                  pages.push(i);
                }

                if (rangeEnd < total - 1) {
                  pages.push('...');
                }

                if (total > 1) {
                  pages.push(total);
                }

                return pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-sm text-gray-500"
                      >
                        …
                      </span>
                    );
                  }

                  const pageNumber = page as number;
                  const isActive = pageNumber === current;

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber - 1)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                });
              })()}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage + 1 === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignsTable;